import { defineStore } from 'pinia'
import api from '@/utils/api'

export const useOrderStore = defineStore('orders', {
  state: () => ({
    orders: [],
    completedOrders: [],
    loading: false,
    error: null,
    lastFetch: null
  }),

  getters: {
    ordersByStatus: (state) => (status) => {
      return state.orders.filter(order => order.mirakl_status === status)
    },

    waitingAcceptanceOrders: (state) => {
      return state.orders.filter(order => order.mirakl_status === 'WAITING_ACCEPTANCE')
    },

    shippingOrders: (state) => {
      return state.orders.filter(order => order.mirakl_status === 'SHIPPING')
    },

    shippedOrders: (state) => {
      return state.orders.filter(order => order.mirakl_status === 'SHIPPED')
    },

    ordersWithErrors: (state) => {
      return state.orders.filter(order => order.sync_errors && order.sync_errors.length > 0)
    }
  },

  actions: {
    async fetchOrders(status = 'all') {
      this.loading = true
      this.error = null
      
      try {
        const response = await api.get('/api/endpoints/orders.php', {
          params: { status }
        })
        
        if (response.data.success) {
          this.orders = response.data.data
          this.lastFetch = new Date()
        } else {
          throw new Error(response.data.error || 'Failed to fetch orders')
        }
      } catch (error) {
        this.error = error.message
        console.error('Error fetching orders:', error)
      } finally {
        this.loading = false
      }
    },

    async fetchCompletedOrders() {
      this.loading = true
      this.error = null
      
      try {
        const response = await api.get('/api/endpoints/orders.php', {
          params: { type: 'completed' }
        })
        
        if (response.data.success) {
          this.completedOrders = response.data.data
        } else {
          throw new Error(response.data.error || 'Failed to fetch completed orders')
        }
      } catch (error) {
        this.error = error.message
        console.error('Error fetching completed orders:', error)
      } finally {
        this.loading = false
      }
    },

    async acceptOrder(orderId) {
      try {
        const response = await api.post('/api/endpoints/order-actions.php', {
          order_id: orderId,
          action: 'accept'
        })
        
        if (response.data.success) {
          // Update order in local state
          const order = this.orders.find(o => o.id === orderId)
          if (order) {
            order.mirakl_status = 'SHIPPING'
          }
          return true
        } else {
          throw new Error(response.data.error || 'Failed to accept order')
        }
      } catch (error) {
        this.error = error.message
        throw error
      }
    },

    async rejectOrder(orderId, reason = 'Out of stock') {
      try {
        const response = await api.post('/api/endpoints/order-actions.php', {
          order_id: orderId,
          action: 'reject',
          reason
        })
        
        if (response.data.success) {
          // Remove order from local state or update status
          const orderIndex = this.orders.findIndex(o => o.id === orderId)
          if (orderIndex !== -1) {
            this.orders[orderIndex].mirakl_status = 'REFUSED'
          }
          return true
        } else {
          throw new Error(response.data.error || 'Failed to reject order')
        }
      } catch (error) {
        this.error = error.message
        throw error
      }
    },

    getOrderById(orderId) {
      return this.orders.find(order => order.id === orderId) ||
             this.completedOrders.find(order => order.id === orderId)
    }
  }
})